import React, { useEffect, useRef } from "react";

const ParticleAnimation = () => {
  const canvasRef = useRef(null);

  // Constants for easy experimentation
  const TIME_SCALE = 50000;
  const NOISE_SCALE = 0.0005;
  const MOVEMENT_AMPLITUDE = 0.01;
  const STREAM_GRAVITY = 0.001;
  const VELOCITY_DAMPING = 0.99;
  const MAX_VELOCITY = 0.5;
  const NUM_PARTICLES_INVERSE = 30000;
  const PARTICLE_SIZE = 5;
  const STREAM_WIDTH = 0.5;
  const CONNECTION_STRENGTH = 0.05;
  const MAX_CONNECTION_DISTANCE = 70;
  const STREAM_THRESHOLD = 100;
  const STREAM_SPEED = 0.05;
  const PARTICLE_BASE_SPEED = 0.01;

  function noise(x, y) {
    const X = Math.floor(x) & 255;
    const Y = Math.floor(y) & 255;
    return (Math.sin(X * 12.9898 + Y * 78.233) * 43758.5453) % 1;
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let width, height;
    let particles = [];

    let prevWidth = canvas.width;
    let prevHeight = canvas.height;

    function resizeCanvas() {
      const newWidth = canvas.offsetWidth;
      const newHeight = canvas.offsetHeight;
      if (
        Math.abs(newWidth - prevWidth) > 50 ||
        Math.abs(newHeight - prevHeight) > 50
      ) {
        width = canvas.width = newWidth;
        height = canvas.height = newHeight;
        initParticles();
        prevWidth = newWidth;
        prevHeight = newHeight;
      }
    }

    function initParticles() {
      particles = [];
      const numParticles = Math.floor((width * height) / NUM_PARTICLES_INVERSE);
      for (let i = 0; i < numParticles; i++) {
        particles.push({
          x: Math.random() * width,
          y: Math.random() * height,
          size: Math.random() * PARTICLE_SIZE + 1.0,
          vx: 0,
          vy: 0,
          baseOpacity: Math.random() * 0.5 + 0.5,
        });
      }
    }

    function animate() {
      ctx.clearRect(0, 0, width, height);

      const time = Date.now() / TIME_SCALE;

      // Varying frequency and amplitude
      const baseFrequency = 0.01 + Math.sin(time * 0.05) * 0.0003;
      const baseAmplitude = 0.1 + Math.sin(time * 0.03) * 0.05;

      particles.forEach((particle) => {
        // Calculate the stream's y-position for this particle's x-position
        const streamY =
          Math.sin(particle.x * baseFrequency) * height * baseAmplitude;
        const targetY = height / 2 + streamY;

        const distanceFromStream = Math.abs(particle.y - targetY);
        const streamInfluence = Math.max(
          0,
          1 - distanceFromStream / (height * STREAM_WIDTH),
        );

        const noiseX = noise(
          particle.x * NOISE_SCALE + time,
          particle.y * NOISE_SCALE,
        );
        const noiseY = noise(
          particle.x * NOISE_SCALE + time + 1000,
          particle.y * NOISE_SCALE,
        );

        const movementX = (noiseX - 0.5) * MOVEMENT_AMPLITUDE;
        const movementY = (noiseY - 0.5) * MOVEMENT_AMPLITUDE;

        // Add constant rightward movement
        particle.vx +=
          PARTICLE_BASE_SPEED + STREAM_SPEED * streamInfluence + movementX;

        // Apply vertical stream force with reduced influence if particle is inside the threshold
        if (distanceFromStream > STREAM_THRESHOLD) {
          particle.vy +=
            (targetY - particle.y) * STREAM_GRAVITY * streamInfluence;
        } else {
          particle.vy +=
            (targetY - particle.y) * STREAM_GRAVITY * streamInfluence * 0.1;
        }
        particle.vy += movementY;

        // Apply connections
        particles.forEach((otherParticle) => {
          if (particle !== otherParticle) {
            const dx = otherParticle.x - particle.x;
            const dy = otherParticle.y - particle.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            if (distance < MAX_CONNECTION_DISTANCE) {
              const noiseFactor = noise(
                particle.x * NOISE_SCALE,
                particle.y * NOISE_SCALE,
              );
              const force =
                (distance - MAX_CONNECTION_DISTANCE) *
                CONNECTION_STRENGTH *
                (1 + noiseFactor * 400);
              particle.vx += (dx / distance) * force;
              particle.vy += (dy / distance) * force;
            }
          }
        });

        particle.x += particle.vx;
        particle.y += particle.vy;

        particle.vx *= VELOCITY_DAMPING;
        particle.vy *= VELOCITY_DAMPING;

        const speed = Math.sqrt(
          particle.vx * particle.vx + particle.vy * particle.vy,
        );
        if (speed > MAX_VELOCITY) {
          particle.vx = (particle.vx / speed) * MAX_VELOCITY;
          particle.vy = (particle.vy / speed) * MAX_VELOCITY;
        }

        // Improved edge handling
        if (particle.x > width) particle.x = 0;
        if (particle.x < 0) particle.x = width;
        if (particle.y > height) particle.y = 0;
        if (particle.y < 0) particle.y = height;

        const fadeStart = height * 0.6;
        let opacity = particle.baseOpacity;
        if (particle.y > fadeStart) {
          opacity *= 1 - (particle.y - fadeStart) / (height - fadeStart);
        } else {
          opacity = 1;
        }

        const centerX = width / 2;
        const centerY = height / 2;
        const horizontalRadius = width * 0.4;
        const verticalRadius = height * 0.2;
        const dx = Math.abs(particle.x - centerX);
        const dy = Math.abs(particle.y - centerY);
        const distanceFromCenter = Math.sqrt(
          Math.pow(dx / horizontalRadius, 2) + Math.pow(dy / verticalRadius, 2),
        );

        // Smooth transition for center opacity
        const centerFadeStart = 0.8; // Start fading at 80% of the distance to the edge
        const centerFadeEnd = 1.2; // End fading at 120% of the distance to the edge
        if (distanceFromCenter < centerFadeEnd) {
          const fadeFactor = Math.min(
            1,
            Math.max(
              0,
              (distanceFromCenter - centerFadeStart) /
                (centerFadeEnd - centerFadeStart),
            ),
          );
          opacity *= 0.1 + fadeFactor * 0.9; // Fade from 10% to 100% opacity
        }

        ctx.fillStyle = `rgba(15, 45, 45, ${opacity})`;
        ctx.fillRect(particle.x, particle.y, particle.size, particle.size);
      });

      // Draw connections
      ctx.strokeStyle = "rgba(15, 45, 45, 0.3)";
      ctx.lineWidth = 0.3;
      particles.forEach((particle, index) => {
        for (let i = index + 1; i < particles.length; i++) {
          let otherParticle = particles[i];
          let dx = particle.x - otherParticle.x;
          let dy = particle.y - otherParticle.y;
          let distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < MAX_CONNECTION_DISTANCE) {
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(otherParticle.x, otherParticle.y);
            ctx.stroke();
          }
        }
      });

      requestAnimationFrame(animate);
    }

    function debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }

    const debouncedResizeCanvas = debounce(resizeCanvas, 250);
    window.addEventListener("resize", debouncedResizeCanvas);

    resizeCanvas();
    animate();

    return () => {
      window.removeEventListener("resize", debouncedResizeCanvas);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "70vh",
        zIndex: 1,
      }}
    />
  );
};

export default ParticleAnimation;
